import React, { useState, useRef, useEffect, useCallback } from 'react';
import { HiChevronDown, HiCheck, HiExclamation } from 'react-icons/hi';
import CustomTooltip from '../CustomTooltip';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  disabledReason?: string;
}

interface CompactSelectProps {
  options: SelectOption[];
  value: string;
  onChange: (value: string | null) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  width?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  'aria-label'?: string;
  customHeader?: React.ReactNode;
  lookupOptions?: SelectOption[];
  searchSlot?: React.ReactNode;
}

const CompactSelect: React.FC<CompactSelectProps> = ({
  options = [],
  value,
  onChange,
  placeholder = "Select an option",
  className = "",
  disabled = false,
  width = "min-w-[100px]",
  searchable = false,
  searchPlaceholder = 'Search...',
  'aria-label': ariaLabel,
  customHeader,
  lookupOptions,
  searchSlot,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const listRef = useRef<HTMLDivElement>(null);
  const typeaheadRef = useRef<{ query: string; timeoutId: number | null }>({ query: '', timeoutId: null });
  const [searchQuery, setSearchQuery] = useState('');

  // Find selected option with null safety - check lookupOptions first, then options
  const selectedOption = (lookupOptions || options).find(opt => opt?.value === value);

  const visibleOptions = React.useMemo(() => {
    if (!searchable) return options;
    const trimmedQuery = searchQuery.trim().toLowerCase();
    if (!trimmedQuery) return options;
    return options.filter(opt => opt?.label?.toLowerCase().includes(trimmedQuery));
  }, [options, searchQuery, searchable]);

  const getIndexByValue = useCallback((val: string | undefined) => {
    if (!val) return -1;
    return visibleOptions.findIndex(opt => opt?.value === val);
  }, [visibleOptions]);

  const moveHighlight = useCallback((direction: 1 | -1) => {
    if (!visibleOptions.length) return;
    setHighlightedIndex(prev => {
      const next = prev < 0 ? (direction === 1 ? 0 : visibleOptions.length - 1) : (prev + direction + visibleOptions.length) % visibleOptions.length;
      return next;
    });
  }, [visibleOptions.length]);

  const scrollHighlightedIntoView = useCallback(() => {
    if (!listRef.current || highlightedIndex < 0) return;
    const listEl = listRef.current;
    const itemEl = listEl.querySelector<HTMLButtonElement>(`[data-index="${highlightedIndex}"]`);
    if (itemEl) {
      itemEl.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  useEffect(() => {
    scrollHighlightedIntoView();
  }, [highlightedIndex, isOpen, scrollHighlightedIntoView]);

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // When opening, initialize highlighted item to selected option or first item
  useEffect(() => {
    if (isOpen) {
      const idx = getIndexByValue(value);
      setHighlightedIndex(idx >= 0 ? idx : (visibleOptions.length ? 0 : -1));
    } else {
      setHighlightedIndex(-1);
    }
  }, [isOpen, value, visibleOptions, getIndexByValue]);

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
    }
  }, [isOpen]);

  const handleToggle = useCallback(() => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  }, [isOpen, disabled]);

  const commitSelection = useCallback((index: number) => {
    if (index < 0 || index >= visibleOptions.length) return;
    const option = visibleOptions[index];
    if (!option?.value || option.disabled) return;
    onChange(option.value);
    setIsOpen(false);
  }, [visibleOptions, onChange]);

  const handleOptionSelect = useCallback((option: SelectOption, index?: number) => {
    if (!option?.value || option.disabled) return;
    onChange(option.value);
    setIsOpen(false);
    if (typeof index === 'number') setHighlightedIndex(index);
  }, [onChange]);

  const clearTypeahead = useCallback(() => {
    if (typeaheadRef.current.timeoutId) {
      window.clearTimeout(typeaheadRef.current.timeoutId);
    }
    typeaheadRef.current = { query: '', timeoutId: null };
  }, []);

  const handleTypeahead = useCallback((key: string) => {
    if (searchable && searchQuery.trim()) return false;
    const isCharacterKey = key.length === 1 && !/\s/.test(key);
    if (!isCharacterKey) return false;

    const nextQuery = (typeaheadRef.current.query + key).toLowerCase();
    typeaheadRef.current.query = nextQuery;

    if (typeaheadRef.current.timeoutId) window.clearTimeout(typeaheadRef.current.timeoutId);
    typeaheadRef.current.timeoutId = window.setTimeout(() => {
      clearTypeahead();
    }, 500);

    const startIndex = highlightedIndex >= 0 ? highlightedIndex : 0;
    const searchSpace = visibleOptions.map(o => o?.label?.toLowerCase() || '');

    // Search from current to end, then wrap to start
    let found = searchSpace.findIndex((label, idx) => idx >= startIndex && label.startsWith(nextQuery));
    if (found === -1) {
      found = searchSpace.findIndex((label, idx) => idx < startIndex && label.startsWith(nextQuery));
    }

    if (found !== -1) {
      if (!isOpen) setIsOpen(true);
      setHighlightedIndex(found);
      return true;
    }

    return false;
  }, [visibleOptions, highlightedIndex, isOpen, clearTypeahead, searchable, searchQuery]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (disabled) return;

    // Try typeahead first for character keys
    const key = e.key;
    const triedTypeahead = handleTypeahead(key);
    if (triedTypeahead) {
      e.preventDefault();
      return;
    }

    switch (key) {
      case 'Enter':
        e.preventDefault();
        if (isOpen) {
          commitSelection(highlightedIndex >= 0 ? highlightedIndex : getIndexByValue(value));
        } else {
          setIsOpen(true);
        }
        break;
      case ' ': // Space
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else if (highlightedIndex >= 0) {
          commitSelection(highlightedIndex);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          moveHighlight(1);
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          moveHighlight(-1);
        }
        break;
      case 'Home':
        if (isOpen) {
          e.preventDefault();
          if (visibleOptions.length) setHighlightedIndex(0);
        }
        break;
      case 'End':
        if (isOpen) {
          e.preventDefault();
          if (visibleOptions.length) setHighlightedIndex(visibleOptions.length - 1);
        }
        break;
    }
  }, [disabled, isOpen, highlightedIndex, visibleOptions.length, moveHighlight, commitSelection, getIndexByValue, value, handleTypeahead]);

  return (
    <div ref={selectRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-label={ariaLabel || placeholder}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        className={`w-full ${width} px-1 py-2 text-left bg-white dark:bg-secondary-dark
                   border border-gray-300 dark:border-gray-600 rounded-lg text-sm
                   hover:border-gray-400 dark:hover:border-gray-500
                   focus:outline-none focus:ring-1 focus:ring-gray-300/30 dark:focus:ring-gray-400/30
                   text-gray-900 dark:text-white transition-colors duration-200
                   ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <div className="flex items-center justify-between">
          <span className={selectedOption ? "text-gray-900 dark:text-white" : "text-gray-500 dark:text-gray-400"}>
            {selectedOption?.label || placeholder}
          </span>
          <HiChevronDown
            className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''
              }`}
          />
        </div>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-secondary-dark
                        border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-[9999]
                        overflow-hidden">
          {customHeader && (
            <div className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-secondary-dark">
              {customHeader}
            </div>
          )}
          {searchable && (
            <div className="p-2 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-secondary-dark">
              <div className="flex items-center gap-2">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={searchPlaceholder}
                autoFocus
                  className="flex-1 px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-md
                           bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                           focus:outline-none focus:ring-1 focus:ring-gray-300/30 dark:focus:ring-gray-500/40"
              />
                {searchSlot}
              </div>
            </div>
          )}
          <div
            ref={listRef}
            className="max-h-64 overflow-y-auto"
            role="listbox"
            aria-activedescendant={highlightedIndex >= 0 ? `compact-select-option-${highlightedIndex}` : undefined}
          >
            {visibleOptions.length > 0 ? (
              visibleOptions.map((option, index) => {
                if (option?.value === undefined || option?.value === null || !option?.label) return null;

                const isSelected = option.value === value;
                const isHighlighted = index === highlightedIndex;
                const isOptionDisabled = option.disabled === true;

                const buttonEl = (
                  <button
                    id={`compact-select-option-${index}`}
                    data-index={index}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={isOptionDisabled}
                    onClick={() => !isOptionDisabled && handleOptionSelect(option, index)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    className={`w-full px-3 py-2 text-sm transition-colors duration-200
                               text-left
                               ${isOptionDisabled
                                 ? 'cursor-not-allowed text-gray-400 dark:text-gray-500'
                                 : `cursor-pointer text-gray-700 dark:text-gray-300 ${isHighlighted ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`
                               }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5 truncate flex-1 min-w-0">
                        {isOptionDisabled && (
                          <HiExclamation className="w-4 h-4 text-amber-500 flex-shrink-0" />
                        )}
                        <span className="truncate">{option.label}</span>
                      </div>
                      {isSelected && !isOptionDisabled && (
                        <HiCheck className="w-4 h-4 text-gray-600 dark:text-gray-400 flex-shrink-0 ml-2" />
                      )}
                    </div>
                  </button>
                );

                if (isOptionDisabled && option.disabledReason) {
                  return (
                    <CustomTooltip key={option.value} title={option.disabledReason} placement="right">
                      {buttonEl}
                    </CustomTooltip>
                  );
                }

                return <React.Fragment key={option.value}>{buttonEl}</React.Fragment>;
              })
            ) : (
              <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                {searchable && searchQuery.trim() ? 'No matches found' : 'No options available'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CompactSelect;